import { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Sparkles, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { useNewsletterSettings } from '@/hooks/useNewsletterSettings';

// Sanitize HTML: only allow safe embed elements
function sanitizeEmbedHtml(html: string): string {
  const div = document.createElement('div');
  div.innerHTML = html;
  div.querySelectorAll('script').forEach(el => el.remove());
  const allowed = ['FORM', 'INPUT', 'BUTTON', 'DIV', 'SPAN', 'LABEL', 'P', 'IFRAME', 'IMG', 'A', 'SELECT', 'OPTION', 'TEXTAREA', 'FIELDSET', 'LEGEND', 'BR', 'HR', 'STYLE'];
  const walkAndClean = (node: Element) => {
    Array.from(node.children).forEach(child => {
      if (!allowed.includes(child.tagName)) {
        child.remove();
      } else {
        walkAndClean(child);
      }
    });
  };
  walkAndClean(div);
  return div.innerHTML;
}

async function subscribeToNewsletter(email: string, apiUrl: string, portalId: string): Promise<{ ok: boolean; error?: string }> {
  try {
    const res = await fetch(`${apiUrl}/newsletter/subscribe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, portal_id: portalId, source: 'website' }),
    });
    const data = await res.json();
    if (!res.ok || !data.ok) {
      return { ok: false, error: data.error || data.message || 'Napaka pri naročanju.' };
    }
    return { ok: true };
  } catch {
    return { ok: false, error: 'Napaka pri povezavi. Poskusite znova.' };
  }
}

export { subscribeToNewsletter };

export function NewsletterCTA() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const { data: settings } = useNewsletterSettings();

  if (settings && !settings.enabled) return null;

  const badgeText = settings?.badge_text || 'Tedenske BUMnovice';
  const title = settings?.title || 'Ne zamudite najbolj branih zgodb tedna';
  const description = settings?.description || 'Prejemajte izbrane novice, trače, zanimivosti in ekskluzivne vsebine neposredno v vaš e-poštni nabiralnik.';
  const emailPlaceholder = settings?.email_placeholder || 'Vnesite svoj e-poštni naslov';
  const buttonText = settings?.button_text || 'Naroči se na novice';
  const finePrint = settings?.fine_print || 'Odjava kadarkoli. Vaše podatke varujemo.';
  const mode = settings?.mode || 'internal';
  const externalHtml = settings?.external_html || '';
  const apiUrl = settings?.api_url || 'https://zoqxgkkqqrgppxrysfsv.supabase.co/functions/v1';
  const portalId = settings?.portal_id || 'bum-novice';

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    // Basic email validation
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error('Vnesite veljaven e-poštni naslov.');
      return;
    }

    setIsLoading(true);
    const result = await subscribeToNewsletter(trimmed, apiUrl, portalId);
    setIsLoading(false);

    if (result.ok) {
      setIsSubscribed(true);
      toast.success('Preverite svojo e-pošto za potrditev naročnine!');
    } else {
      toast.error(result.error || 'Nekaj je šlo narobe. Poskusite znova.');
    }
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="relative overflow-hidden rounded-2xl bg-primary p-8 md:p-12"
    >
      <div className="absolute top-0 right-0 w-64 h-64 bg-secondary/20 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-48 h-48 bg-secondary/10 rounded-full blur-2xl" />
      
      <div className="relative z-10 max-w-2xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 bg-primary-foreground/10 rounded-full px-4 py-1.5 mb-6">
          <Sparkles className="w-4 h-4 text-secondary" />
          <span className="text-primary-foreground text-sm font-medium">{badgeText}</span>
        </div>
        
        <h2 className="font-serif text-3xl md:text-4xl font-bold text-primary-foreground mb-4">
          {title}
        </h2>
        <p className="text-primary-foreground/80 text-lg mb-8">
          {description}
        </p>

        {mode === 'external' && externalHtml ? (
          <div 
            className="max-w-md mx-auto overflow-hidden"
            dangerouslySetInnerHTML={{ __html: sanitizeEmbedHtml(externalHtml) }}
          />
        ) : isSubscribed ? (
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="flex items-center justify-center gap-2 text-primary-foreground"
          >
            <CheckCircle className="w-6 h-6 text-secondary" />
            <span className="text-lg font-medium">Preverite svojo e-pošto za potrditev naročnine!</span>
          </motion.div>
        ) : (
          <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <div className="relative flex-1">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="email"
                placeholder={emailPlaceholder}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10 h-12 bg-primary-foreground border-0"
                required
              />
            </div>
            <Button 
              type="submit" 
              disabled={isLoading}
              className="btn-gold h-12 px-8"
            >
              {isLoading ? 'Naročanje...' : buttonText}
            </Button>
          </form>
        )}

        <p className="text-primary-foreground/60 text-xs mt-4">
          {finePrint}
        </p>
      </div>
    </motion.section>
  );
}
